import { SxProps, Theme } from '@mui/material';

const base: SxProps<Theme> = {
  borderRadius: '999px',
  padding: '16px 24px',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  textTransform: 'none',
  gap: '8px'
};

const primary: SxProps<Theme> = {
  ...base,
  backgroundColor: 'primary.main',
  color: 'white',
  border: 'none',
  '&:hover': {
    backgroundColor: 'customGrey.dark'
  },
  '&:disabled': {
    backgroundColor: 'customGrey.medium',
    color: 'white'
  }
};

const primaryDestructive: SxProps<Theme> = {
  ...base,
  backgroundColor: 'error.main',
  color: 'white',
  border: 'none',
  '&:hover': {
    backgroundColor: 'error.main',
    opacity: 0.9
  }
};

const secondary: SxProps<Theme> = {
  ...base,
  backgroundColor: 'white',
  color: 'primary.main',
  border: (theme) => `1px solid ${theme.palette.primary.main}`,
  '&:hover': {
    backgroundColor: 'white',
    color: 'customGrey.dark',
    borderColor: 'customGrey.dark'
  }
};

const secondaryDestructive: SxProps<Theme> = {
  ...base,
  backgroundColor: 'white',
  color: 'error.main',
  border: (theme) => `1px solid ${theme.palette.error.main}`,
  '&:hover': {
    backgroundColor: 'white',
    color: 'error.main',
    borderColor: 'error.main',
    opacity: 0.9
  }
};

const tertiary: SxProps<Theme> = {
  ...base,
  padding: '4px 8px',
  backgroundColor: 'transparent',
  color: 'primary.main',
  border: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'customGrey.dark'
  }
};

const tertiaryDestructive: SxProps<Theme> = {
  ...base,
  padding: '4px 8px',
  backgroundColor: 'transparent',
  color: 'error.main',
  border: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'error.main',
    opacity: 0.9
  }
};

const link: SxProps<Theme> = {
  padding: 0,
  backgroundColor: 'transparent',
  color: 'primary.main',
  border: 'none',
  textDecoration: 'underline',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '26px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'customGrey.dark',
    textDecoration: 'none'
  }
};

// Hover state styles (persistent hover appearance)
const primaryHover: SxProps<Theme> = {
  ...base,
  backgroundColor: 'customGrey.dark',
  color: 'white',
  border: 'none',
  '&:hover': {
    backgroundColor: 'customGrey.dark'
  }
};

const secondaryHover: SxProps<Theme> = {
  ...base,
  backgroundColor: 'white',
  color: 'customGrey.dark',
  border: (theme) => `1px solid ${theme.palette.customGrey.dark}`,
  '&:hover': {
    backgroundColor: 'white',
    color: 'customGrey.dark',
    borderColor: 'customGrey.dark'
  }
};

const tertiaryHover: SxProps<Theme> = {
  ...base,
  padding: '4px 8px',
  backgroundColor: 'transparent',
  color: 'customGrey.dark',
  border: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'customGrey.dark'
  }
};

const linkHover: SxProps<Theme> = {
  padding: 0,
  backgroundColor: 'transparent',
  color: 'customGrey.dark',
  border: 'none',
  textDecoration: 'none',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '26px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'customGrey.dark',
    textDecoration: 'none'
  }
};

const loadingContainer: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const loadingSpinner: SxProps<Theme> = {
  color: 'primary.main',
  position: 'absolute'
};

const loadingSpinnerInline: SxProps<Theme> = {
  color: 'primary.main'
};

const loadingContent = (loading: boolean): SxProps<Theme> => ({
  opacity: loading ? 0 : 1
});

// Bar loading styles
const barLoadingWrapper: SxProps<Theme> = {
  height: '56px',
  display: 'flex',
  alignItems: 'center'
};

const barLoadingButton: SxProps<Theme> = {
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: 'transparent',
  transition: 'height 0.3s ease, min-height 0.3s ease',
  '&:disabled': {
    backgroundColor: 'transparent'
  }
};

const barLoadingTrack: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  left: 0,
  right: 0,
  transform: 'translateY(-50%)',
  height: '10px',
  backgroundColor: 'primary.light',
  borderRadius: '999px',
  overflow: 'hidden',
  zIndex: 0
};

const barLoadingSlider: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '30px',
  backgroundColor: 'primary.main',
  borderRadius: '999px',
  animation: 'slideLeftRightLoop 1.4s ease-in-out infinite',
  '@keyframes slideLeftRightLoop': {
    '0%': {
      left: 0
    },
    '50%': {
      left: 'calc(100% - 30px)'
    },
    '100%': {
      left: 0
    }
  }
};

export const buttonSx = {
  base,
  primary,
  primaryDestructive,
  primaryHover,
  secondary,
  secondaryDestructive,
  secondaryHover,
  tertiary,
  tertiaryDestructive,
  tertiaryHover,
  link,
  linkHover,
  loadingContainer,
  loadingSpinner,
  loadingSpinnerInline,
  loadingContent,
  barLoadingWrapper,
  barLoadingButton,
  barLoadingTrack,
  barLoadingSlider
};
